import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { PermissionService } from '@services/permission/permission.service';
import { Permission } from 'app/enums';
import { APP_ROUTES } from 'app/constants';

@Injectable({
    providedIn: 'root'
})
export class DefaultNavigationService {
    private readonly permissionService = inject(PermissionService);
    private readonly router = inject(Router);

    /**
     * Reloads the page after a representation change.
     * Tries to stay on the current route if accessible, otherwise goes to default route.
     * Performs a full page reload to ensure all permissions and cached data are refreshed.
     */
    public reloadAfterRepresentationChange(): void {
        const currentUrl = this.router.url;

        // If on login page or root, go to default before reload
        if (currentUrl === `/${APP_ROUTES.Login}` || currentUrl === '/' || currentUrl === '') {
            const defaultRoute = this.getDefaultRoute();
            window.location.href = `/${defaultRoute}`;
            return;
        }

        // Check if current route is likely accessible based on permissions
        if (this.isCurrentRouteAccessible(currentUrl)) {
            // Reload current page
            window.location.reload();
        } else {
            // Navigate to default route (causes a page load)
            const defaultRoute = this.getDefaultRoute();
            window.location.href = `/${defaultRoute}`;
        }
    }

    /**
     * Checks if the current route is likely accessible based on user's permissions.
     */
    private isCurrentRouteAccessible(url: string): boolean {
        // Extract the base route from the URL
        const urlSegments = url
            .split('?')[0]
            .split('/')
            .filter((s) => s);
        if (urlSegments.length === 0) {
            return false;
        }

        const baseRoute = urlSegments[0];

        // Check permissions for each route type
        switch (baseRoute) {
            case APP_ROUTES.Dashboard:
                return this.permissionService.hasAnyPermission([Permission.ReadMeasurement]);
            case APP_ROUTES.Measurement:
                return this.permissionService.hasAnyPermission([Permission.ReadMeasurement]);
            case APP_ROUTES.User:
                return this.permissionService.hasAnyPermission([Permission.ReadUser]);
            case APP_ROUTES.Location:
                return this.permissionService.hasAnyPermission([Permission.ReadLocation]);
            case APP_ROUTES.Device:
                return this.permissionService.hasAnyPermission([Permission.ReadDevice]);
            case APP_ROUTES.Tenant:
                return this.permissionService.hasAnyPermission([Permission.ReadTenant]);
            case APP_ROUTES.FirmwareList:
                return this.permissionService.hasAnyPermission([Permission.WriteFirmware]);
            default:
                // For unknown routes, assume accessible (API will return 403 if not)
                return true;
        }
    }

    /**
     * Navigates to the default route based on the user's permissions.
     * Priority: Dashboard > Measurement > User > Location > Device > Tenant
     */
    public async navigateToDefault(): Promise<boolean> {
        const defaultRoute = this.getDefaultRoute();
        return this.router.navigate([defaultRoute]);
    }

    /**
     * Gets the default route path based on the user's permissions.
     * Returns the first accessible route from the priority list.
     */
    public getDefaultRoute(): string {
        // Check feature-specific permissions in priority order
        if (this.permissionService.hasAnyPermission([Permission.ReadMeasurement])) {
            return APP_ROUTES.Dashboard;
        }
        // if (this.permissionService.hasAnyPermission([Permission.ReadMeasurement])) {
        //     return APP_ROUTES.MeasurementList;
        // }
        if (this.permissionService.hasAnyPermission([Permission.ReadUser])) {
            return APP_ROUTES.UserList;
        }

        if (this.permissionService.hasAnyPermission([Permission.ReadLocation])) {
            return APP_ROUTES.LocationList;
        }

        if (this.permissionService.hasAnyPermission([Permission.ReadDevice])) {
            return APP_ROUTES.DeviceList;
        }

        if (this.permissionService.hasAnyPermission([Permission.ReadTenant])) {
            return APP_ROUTES.TenantList;
        }

        if (this.permissionService.hasAnyPermission([Permission.WriteFirmware])) {
            return APP_ROUTES.DeviceUpgrade;
        }
        // Fallback to user profile if no other permissions exist
        return APP_ROUTES.UserProfile;
    }
}
